import { css, cx } from '@linaria/core'
import React, { use } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Button } from '#components/Button'
import { IconButton } from '#components/IconButton'
import { ReactScan } from '#features/DebugSettings/ReactScan'
import { RequestMonitorButton } from '#features/DebugSettings/RequestMonitor/RequestMonitorButton'
import { useToast } from '#features/Toasts/useToast'
import CloseIcon from '#icons/close-48.svg'
import SettingsIcon from '#icons/settings-48.svg'
import { setForceHoverPopupsOpen } from '#store/componentConfiguration'
import { useSharedAppSelector } from '#store/sharedBaseStore'
import { style } from '#styles'
import { sharedTestIds } from '#utils/sharedTestIds'
import { ClearCaches } from './ClearCaches'
import { InternalDebugPortalContext } from './InternalDebugPortalContext'

const debugSettingsClass = css`
  position: fixed;
  bottom: 15px;
  left: 60px;
  z-index: 54000;

  > ._container {
    position: relative;
    backdrop-filter: blur(5px);
    background: rgba(255, 155, 155, 0.2);
    display: flex;
    flex-direction: column;
    border-radius: 5px;
    padding: 10px;
    gap: 10px;
    z-index: 54001;

    .open-button {
      opacity: 0.2;

      &:focus-within {
        opacity: 1;
      }
    }

    label {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 0 0 4px 0;

      cursor: pointer;

      &:hover {
        color: ${style.theme.textAccent};
      }
    }

    > section {
      margin-left: 10px;
    }

    > .close-button {
      position: absolute;
      top: 10px;
      right: 10px;
    }

    > ._title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 15px;
    }

    > ._request-monitor {
      max-width: 80vw;
    }

    > ._external-debug-controls {
      display: flex;
      flex-direction: column;
      gap: 10px;
      &:empty {
        display: none;
      }
    }

    > ._conflict-creator {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
  }

  .is-open {
    background: rgba(255, 155, 155, 0.4);

    &:hover {
      > .open-button {
        opacity: 1;
      }
    }
  }

  @media print {
    display: none;
  }
`

export const DebugSettingsPopup = () => {
  const [isDebugSettingsOpen, setIsDebugSettingsOpen] = React.useState(false)
  const { add: addToast } = useToast()
  const { t } = useTranslation()
  const { forceHoverPopupsOpen } = useSharedAppSelector((x) => x.componentConfiguration)
  const navigate = useNavigate()

  const dispatch = useDispatch()

  const debugControlContext = use(InternalDebugPortalContext)

  if (!isDebugSettingsOpen) {
    return (
      <div className={cx(debugSettingsClass, 'is-closed')} data-testid={sharedTestIds.debugSettings.popupButton}>
        <div className="_container">
          <IconButton
            iconSize="m"
            className="open-button"
            title={t('debug.settings.button.open', 'Open Debug Settings')}
            iconUrl={SettingsIcon}
            onClick={() => {
              setIsDebugSettingsOpen(true)
            }}
          />
        </div>
      </div>
    )
  }

  return (
    <div className={cx(debugSettingsClass, 'is-open')} data-testid={sharedTestIds.debugSettings.popup}>
      <div className="_container">
        <div className="_title">
          <h3>Debug Settings</h3>
          <IconButton
            className="close-button"
            iconUrl={CloseIcon}
            title={t('shared:common.button.close')}
            onClick={(e) => {
              setIsDebugSettingsOpen(false)
              e.preventDefault()
              e.stopPropagation()
              // remove focus from the button
              window.focus()
            }}
            data-testid={sharedTestIds.debugSettings.popupCloseButton}
          />
        </div>
        <section className="_request-monitor">
          <RequestMonitorButton />
        </section>

        <section>
          <ReactScan />
        </section>

        <section className="_external-debug-controls" ref={debugControlContext.setDebugElementsRoot} />

        <section>
          <Button
            size="xs"
            onClick={() => {
              addToast({
                appearance: 'primary',
                title: 'Important!',
                visibleInMs: 5000,
                children: <p>Here could be your text</p>,
              })
            }}
          >
            Create Toast
          </Button>
        </section>
        <section>
          <Button
            size="xs"
            onClick={() => {
              dispatch(setForceHoverPopupsOpen(!forceHoverPopupsOpen))
            }}
          >
            Toggle force showing all hover tooltip ({forceHoverPopupsOpen ? 'on' : 'off'})
          </Button>
        </section>
        <section>
          <ClearCaches />
        </section>
        <section>
          <Button
            size="xs"
            onClick={() => {
              navigate('/bridge')
            }}
            data-testid={sharedTestIds.debugSettings.bridgeButton}
          >
            Go to Bridge
          </Button>
        </section>
      </div>
    </div>
  )
}
